import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { BadgePercent, Lock, Mail, Phone, User } from 'lucide-react';
import { Button, Card, Input } from '../components';
import { useRegister } from '../hooks/useAuth';
import { byoeApi } from '../services/byoeApi';

/**
 * Landingspagina van de BYOE-uitnodigingslink (migratie 202).
 *
 * Een professional heeft deze opdrachtgever uitgenodigd. Hier maakt de
 * opdrachtgever een account aan met het uitgenodigde e-mailadres en bevestigt
 * de bestaande samenwerking (sleutel 1 van de Double-Key). Daarna volgt de
 * gewone onboarding (KvK + overeenkomst); vervolgens bevestigt de professional.
 */
export const ByoeInvitePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const registerMutation = useRegister();

  const { data: info, isLoading, isError } = useQuery({
    queryKey: ['byoe-invite', token],
    queryFn: () => byoeApi.uitnodiging(token),
    retry: false,
  });

  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', password: '', confirm: '' });
  const [akkoord, setAkkoord] = useState(false);
  const [voorwaarden, setVoorwaarden] = useState(false);

  // Contactnaam voorinvullen met wat de professional opgaf; de opdrachtgever mag corrigeren.
  useEffect(() => {
    if (!info?.contact_naam) return;
    const [voornaam, ...rest] = info.contact_naam.trim().split(/\s+/);
    setForm((f) => ({ ...f, first_name: voornaam ?? '', last_name: rest.join(' ') }));
  }, [info]);

  const zet = (veld: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [veld]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!info) return;
    if (form.first_name.trim().length < 2 || form.last_name.trim().length < 2) {
      toast.error('Vul uw voor- en achternaam in');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 karakters lang zijn');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }
    registerMutation.mutate(
      {
        email: info.email,
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || undefined,
        role: 'opdrachtgever',
        byoe_token: token,
        byoe_akkoord: akkoord,
      },
      { onSuccess: () => navigate('/employer/onboarding', { replace: true }) }
    );
  };

  const backendErrors = (registerMutation.error as any)?.response?.data?.errors as string[] | undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Uitnodigingslink ongeldig</h1>
          <p className="text-gray-600 mb-6">
            Deze link is verlopen (7 dagen geldig) of al gebruikt. Vraag uw professional om een nieuwe uitnodiging.
          </p>
          <Button onClick={() => navigate('/login')}>Naar inloggen</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-gray-50">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/belagom-text.png" alt="BeLagom" className="h-10 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Account aanmaken voor {info.bedrijfsnaam}</h1>
          <p className="mt-2 text-gray-600">
            <strong>{info.professional_naam}</strong> nodigt u uit om uw samenwerking voort te zetten via BeLagom.
            Maak hieronder kosteloos een account aan; daarna rondt u de onboarding (KvK en overeenkomst) af.
          </p>
        </div>

        <div className="mb-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <BadgePercent className="w-5 h-5 shrink-0 mt-0.5" />
          <p>
            De eerste 6 maanden betaalt u <strong>{info.fee_pct}%</strong> in plaats van {info.standaard_fee_pct}%
            platform-opslag op het tarief van {info.professional_naam}.
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-5">
            {backendErrors && backendErrors.length > 0 && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 space-y-1">
                {backendErrors.map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
              </div>
            )}

            <Input label="Zakelijk e-mailadres" type="email" icon={<Mail />} value={info.email} disabled readOnly />

            <div className="grid grid-cols-2 gap-4">
              <Input label="Voornaam" icon={<User />} value={form.first_name} onChange={zet('first_name')} required />
              <Input label="Achternaam" icon={<User />} value={form.last_name} onChange={zet('last_name')} required />
            </div>

            <Input label="Telefoonnummer (optioneel)" type="tel" icon={<Phone />} value={form.phone} onChange={zet('phone')} />
            <Input label="Wachtwoord" type="password" icon={<Lock />} value={form.password} onChange={zet('password')} required minLength={8} />
            <Input label="Bevestig wachtwoord" type="password" icon={<Lock />} value={form.confirm} onChange={zet('confirm')} required minLength={8} />

            <label className="flex items-start gap-2 text-sm text-gray-900">
              <input
                type="checkbox"
                required
                checked={akkoord}
                onChange={(e) => setAkkoord(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span>{info.akkoord_tekst}</span>
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-900">
              <input
                type="checkbox"
                required
                checked={voorwaarden}
                onChange={(e) => setVoorwaarden(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span>
                Ik ga akkoord met de{' '}
                <Link to="/terms" className="text-blue-600 hover:text-blue-500">algemene voorwaarden</Link> en het{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-500">privacybeleid</Link>
              </span>
            </label>

            <Button type="submit" className="w-full" loading={registerMutation.isPending}>
              Account aanmaken
            </Button>
            <p className="text-xs text-gray-500 text-center">
              De verlaagde opslag gaat in zodra {info.professional_naam} de samenwerking na uw onboarding bevestigt.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ByoeInvitePage;
